import React from 'react';
import { Card, Avatar, Spin, Typography, Alert, Button, Tag, Empty, Modal, Radio, Input, Tooltip, message } from 'antd';
import { UserOutlined, ArrowLeftOutlined, LinkOutlined, StopOutlined, TrophyOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useDrupiApi } from '../../hooks/use-drupi-api';
import { UserPostsResponse, UserPrizesResponse, Prize } from './types';

const { Title, Text } = Typography;

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

// Motivos de invalidación que elige la marca. "Otro" habilita texto libre. El motivo se
// guarda en el task y se muestra en el thumb invalidado de Social (mis-publicaciones).
const MOTIVOS_INVALIDACION = [
  'No tiene a LUZU TV como tema principal.',
  'Incumple las normas de TikTok',
  'Contenido ofensivo, engañoso o ilegal.',
  'Otro'
];

// Estilo blindado para que los Tags nunca se estiren (algún CSS global de la app los
// estira en contextos flex). inline-block + altura fija = tamaño natural siempre.
const TAG_STYLE: React.CSSProperties = {
  display: 'inline-block',
  verticalAlign: 'middle',
  height: 22,
  lineHeight: '20px',
  margin: '0 6px 4px 0'
};

// ── Vistas por país (MOCK "Coming soon") ────────────────────────────────────
// DATOS DE EJEMPLO fijos: desglose de vistas por país agregado sobre todas las
// publicaciones que el amplificador vinculó. Los datos reales van a salir del
// field `audience_countries` (% de viewers por video) de la Accounts API de
// TikTok (`/business/video/list/`, scope video.insights) — pendiente de
// aprobación del scope. Hasta entonces, esta card es solo un preview visual.
const VISTAS_PAIS_MOCK = [
  { pais: 'Argentina', flag: '🇦🇷', pct: 35, vistas: 43120 },
  { pais: 'Colombia', flag: '🇨🇴', pct: 14, vistas: 17250 },
  { pais: 'México', flag: '🇲🇽', pct: 12, vistas: 14780 },
  { pais: 'Chile', flag: '🇨🇱', pct: 9, vistas: 11090 },
  { pais: 'Perú', flag: '🇵🇪', pct: 7, vistas: 8620 },
  { pais: 'Estados Unidos', flag: '🇺🇸', pct: 6, vistas: 7390 },
  { pais: 'España', flag: '🇪🇸', pct: 5, vistas: 6160 },
  { pais: 'Uruguay', flag: '🇺🇾', pct: 4, vistas: 4930 },
  { pais: 'Otros', flag: '🌍', pct: 8, vistas: 9860 }
];

export const SocialAdminUserPostsPage: React.FC = () => {
  const { openId } = useParams<{ openId: string }>();
  const { drupiGet, drupiPost } = useDrupiApi();
  const navigate = useNavigate();

  const [data, setData] = React.useState<UserPostsResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [invalidating, setInvalidating] = React.useState<string | null>(null);
  const [prizes, setPrizes] = React.useState<Prize[]>([]);
  // Modal de invalidar: task_id abierto + motivo elegido (+ texto libre para "Otro").
  const [moderating, setModerating] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState<string>(MOTIVOS_INVALIDACION[0]);
  const [reasonOtro, setReasonOtro] = React.useState<string>('');

  React.useEffect(() => {
    if (!openId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    drupiGet<UserPostsResponse>(`/brand-admin/user/${encodeURIComponent(openId)}/posts`)
      .then(d => {
        if (alive) setData(d);
      })
      .catch(e => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [drupiGet, openId]);

  // Premios del amplificador en esta marca (independiente de los posts; no bloquea).
  React.useEffect(() => {
    if (!openId) return;
    let alive = true;
    drupiGet<UserPrizesResponse>(`/brand-admin/user/${encodeURIComponent(openId)}/prizes`)
      .then(d => {
        if (alive) setPrizes(d.prizes || []);
      })
      .catch(() => {
        /* best-effort: si falla, no rompemos la vista de posts */
      });
    return () => {
      alive = false;
    };
  }, [drupiGet, openId]);

  const handleInvalidate = async (taskId: string, motivo: string) => {
    setInvalidating(taskId);
    try {
      await drupiPost(`/brand-admin/posts/${encodeURIComponent(taskId)}/moderate`, { reason: motivo });
      setData(prev =>
        prev
          ? {
              ...prev,
              posts: prev.posts.map(p =>
                p.task_id === taskId ? { ...p, moderated: true, moderated_reason: motivo } : p
              )
            }
          : prev
      );
      setModerating(null);
      message.success('Post invalidado: ya no suma puntos, acá ni en Social.');
    } catch (e) {
      message.error((e as Error).message || 'No se pudo invalidar');
    } finally {
      setInvalidating(null);
    }
  };

  // "Ver post" marca el post como REVISADO por la marca (borde verde). Optimista en el front
  // + best-effort en backend; no bloquea la apertura del link (no hay preventDefault).
  const markReviewed = (taskId: string) => {
    setData(prev =>
      prev
        ? {
            ...prev,
            posts: prev.posts.map(p => (p.task_id === taskId ? { ...p, reviewed: true } : p))
          }
        : prev
    );
    drupiPost(`/brand-admin/posts/${encodeURIComponent(taskId)}/reviewed`).catch(() => {
      /* best-effort */
    });
  };

  const fmt = (n: number | null) => (n === null || n === undefined ? '—' : n.toLocaleString('es-AR'));

  // Momento en que se registró el video para trackear desde Brands (created_at del task).
  // El backend lo guarda en UTC SIN sufijo (datetime.utcnow().isoformat()) → le agregamos
  // 'Z' para marcarlo como UTC, y lo mostramos pinneado a hora de Argentina (no depende del
  // timezone del browser de quien mira la marca).
  const fmtFecha = (iso: string | null) =>
    iso
      ? new Date(iso + 'Z').toLocaleString('es-AR', {
          day: '2-digit',
          month: 'short',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'America/Argentina/Buenos_Aires'
        })
      : '—';

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Button
        type='text'
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/social-admin')}
        style={{ marginBottom: 8, paddingLeft: 0 }}
      >
        Volver
      </Button>

      {error && (
        <Alert style={{ marginBottom: 16 }} type='error' showIcon message='No se pudo cargar' description={error} />
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : data ? (
        <>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            {data.accounts.map(a => (
              <span key={a.social_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Avatar size='small' src={a.avatar_url || undefined} icon={<UserOutlined />} />
                <Text strong>{a.username || a.social_id}</Text>
                <Tag style={TAG_STYLE}>{a.platform}</Tag>
              </span>
            ))}
          </div>

          {/* Resumen: puntos del mes (mismo criterio que Social) + total de videos + cuántos
              invalidados. Derivado de los posts que ya trae el endpoint (metrics.points / moderated). */}
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Puntos del mes</div>
              <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
                {fmt(data.posts.reduce((s, p) => s + (p.metrics?.points || 0), 0))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Videos</div>
              <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{data.posts.length}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Invalidados</div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: data.posts.some(p => p.moderated) ? '#cf1322' : undefined
                }}
              >
                {data.posts.filter(p => p.moderated).length}
              </div>
            </div>
          </div>

          {/* Vistas por país (MOCK) — total agrupado de todas las publicaciones vinculadas.
              Coming soon: datos de ejemplo hasta que se apruebe el scope video.insights
              de la Accounts API (ver VISTAS_PAIS_MOCK arriba). */}
          <Card style={{ marginBottom: 20 }} styles={{ body: { padding: 16 } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <GlobalOutlined style={{ color: '#1677ff', fontSize: 16 }} />
              <Text strong style={{ fontSize: 15 }}>
                Vistas por país
              </Text>
              <Tag color='blue' style={TAG_STYLE}>
                Coming soon
              </Tag>
              <Tag color='orange' style={TAG_STYLE}>
                Datos de ejemplo
              </Tag>
              <Text type='secondary' style={{ fontSize: 12 }}>
                · total agrupado de todas sus publicaciones
              </Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 560 }}>
              {VISTAS_PAIS_MOCK.map(v => (
                <div key={v.pais} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ width: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {v.flag} {v.pais}
                  </span>
                  <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 14, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${v.pct}%`,
                        height: '100%',
                        background: '#1677ff',
                        borderRadius: 4,
                        opacity: 0.85
                      }}
                    />
                  </div>
                  <span style={{ width: 42, textAlign: 'right', fontWeight: 600 }}>{v.pct}%</span>
                  <span style={{ width: 76, textAlign: 'right', color: '#8c8c8c' }} title='Vistas estimadas'>
                    {v.vistas.toLocaleString('es-AR')}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Datos de pago que cargó el amplificador (nombre / email / alias de Mercado Pago)
              para poder transferirle sus premios. Vienen de UserProfilesRepository
              (Social Settings). PayPal quedó deprecado → no se muestra. */}
          <Card style={{ marginBottom: 20 }} styles={{ body: { padding: 16 } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <UserOutlined style={{ color: '#7c3aed', fontSize: 16 }} />
              <Text strong style={{ fontSize: 15 }}>
                Datos de pago
              </Text>
            </div>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Nombre</div>
                <div style={{ fontSize: 14 }}>
                  {data.payment?.full_name ? <b>{data.payment.full_name}</b> : <Text type='secondary'>Sin cargar</Text>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Email</div>
                <div style={{ fontSize: 14 }}>
                  {data.payment?.email ? <b>{data.payment.email}</b> : <Text type='secondary'>Sin cargar</Text>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Alias de Mercado Pago</div>
                <div style={{ fontSize: 14 }}>{data.payment?.mercadopago_alias ? <b>{data.payment.mercadopago_alias}</b> : <Text type='secondary'>Sin cargar</Text>}</div>
              </div>
            </div>
          </Card>

          {/* Premios — milestones que el amplificador alcanzó por mes en tu marca.
              Los genera el PrizesWorker al cierre de mes (o el disparador manual de /admin
              en Social). 100k pts = US$100, tope US$500. */}
          <Card
            style={{ marginBottom: 20, background: '#fffbe6', border: '1px solid #ffe58f' }}
            styles={{ body: { padding: 16 } }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: prizes.length ? 12 : 0,
                flexWrap: 'wrap'
              }}
            >
              <TrophyOutlined style={{ color: '#d48806', fontSize: 18 }} />
              <Text strong style={{ fontSize: 15 }}>
                Premios
              </Text>
              <Text type='secondary' style={{ fontSize: 12 }}>
                · por mes — 100k pts = US$100, tope US$500
              </Text>
            </div>
            {prizes.length === 0 ? (
              <Text type='secondary' style={{ fontSize: 13 }}>
                Todavía no tiene premios en tu marca.
              </Text>
            ) : (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {prizes.map(p => (
                  <div
                    key={`${p.year}-${p.month}`}
                    style={{
                      border: '1px solid #ffe58f',
                      borderRadius: 10,
                      padding: '10px 14px',
                      background: '#fff',
                      minWidth: 150
                    }}
                  >
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                      {MESES[p.month - 1]} {p.year}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#d48806', lineHeight: 1.2 }}>
                      US${p.prize_usd}
                    </div>
                    <div style={{ fontSize: 12, color: '#595959' }}>
                      {fmt(p.points)} pts · milestone {p.milestone}/5
                    </div>
                    <Tag color={p.status === 'pending' ? 'gold' : 'green'} style={{ ...TAG_STYLE, marginTop: 6 }}>
                      {p.status === 'pending' ? 'a cobrar' : p.status}
                    </Tag>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Title level={4}>Videos que amplificó de tu marca</Title>

          {data.posts.length === 0 ? (
            <Empty description='Este usuario todavía no amplificó contenido de tu marca' />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 16,
                alignItems: 'start'
              }}
            >
              {data.posts.map(p => (
                <Card
                  key={p.task_id}
                  size='small'
                  styles={{ body: { padding: 12 } }}
                  // Borde-semáforo: rojo invalidado, verde revisado (apretaron "Ver post"),
                  // amarillo sin revisar todavía (pendiente de mirar).
                  style={{ border: `2px solid ${p.moderated ? '#ff4d4f' : p.reviewed ? '#52c41a' : '#faad14'}` }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    {p.moderated ? (
                      <div
                        title={p.moderated_reason ? `Motivo: ${p.moderated_reason}` : undefined}
                        style={{
                          width: 64,
                          height: 86,
                          borderRadius: 6,
                          background: '#fff1f0',
                          border: '1px solid #ffccc7',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          fontSize: 9,
                          color: '#cf1322',
                          padding: 4,
                          flexShrink: 0,
                          overflow: 'hidden'
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>Invalidada</span>
                        {p.moderated_reason && <span style={{ marginTop: 2 }}>{p.moderated_reason}</span>}
                      </div>
                    ) : p.thumbnail_url ? (
                      <img
                        src={p.thumbnail_url}
                        alt=''
                        style={{ width: 64, height: 86, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 64,
                          height: 86,
                          borderRadius: 6,
                          background: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          color: '#999',
                          flexShrink: 0
                        }}
                      >
                        sin thumb
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: 12
                        }}
                      >
                        {p.caption || ''}
                      </Text>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 14,
                      marginTop: 8,
                      fontSize: 12,
                      color: '#555',
                      alignItems: 'center'
                    }}
                  >
                    {/* Puntos del mes — va primero y resaltado, igual que en Social
                        (mis-publicaciones). Es la métrica que importa: las vistas que el
                        post ganó este mes. Invalidado → 0 (el backend ya lo calcula así). */}
                    <Tooltip title='Puntos que suma este mes — 2 vistas ganadas = 1 punto; cuentan desde que se registró la publicación y se reinician a principio de mes'>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontWeight: 700,
                          cursor: 'default'
                        }}
                      >
                        ✨ {fmt(p.metrics.points)}
                        <span style={{ fontWeight: 600, fontSize: 11 }}>pts</span>
                      </span>
                    </Tooltip>
                    <span title='Views'>▶ {fmt(p.metrics.views)}</span>
                    <span title='Likes'>♥ {fmt(p.metrics.likes)}</span>
                    <span title='Comentarios'>💬 {fmt(p.metrics.comments)}</span>
                    <span title='Shares'>↗ {fmt(p.metrics.shares)}</span>
                  </div>
                  <div
                    style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}
                    title='Momento en que se registró el video para trackear desde Brands (hora Argentina)'
                  >
                    🕒 Registrado: {fmtFecha(p.created_at)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    {p.result_url ? (
                      <Button
                        type='link'
                        size='small'
                        icon={<LinkOutlined />}
                        href={p.result_url}
                        target='_blank'
                        style={{ paddingLeft: 0 }}
                        onClick={() => markReviewed(p.task_id)}
                      >
                        Ver post
                      </Button>
                    ) : (
                      <span />
                    )}
                    {!p.moderated && (
                      <Button
                        type='text'
                        size='small'
                        danger
                        icon={<StopOutlined />}
                        loading={invalidating === p.task_id}
                        onClick={() => {
                          setModerating(p.task_id);
                          setReason(MOTIVOS_INVALIDACION[0]);
                          setReasonOtro('');
                        }}
                      >
                        Invalidar
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : null}

      <Modal
        title='Invalidar publicación'
        open={moderating !== null}
        onCancel={() => setModerating(null)}
        onOk={() => moderating && handleInvalidate(moderating, reason === 'Otro' ? reasonOtro.trim() : reason)}
        okText='Invalidar'
        okButtonProps={{
          danger: true,
          disabled: reason === 'Otro' && !reasonOtro.trim(),
          loading: invalidating === moderating
        }}
        cancelText='Cancelar'
        destroyOnClose
      >
        <p style={{ marginTop: 0 }}>
          Vas a <b>invalidar los puntos</b> de este post (deja de sumar acá y en Social). Elegí el motivo:
        </p>
        <Radio.Group
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {MOTIVOS_INVALIDACION.map(m => (
            <Radio key={m} value={m}>
              {m}
            </Radio>
          ))}
        </Radio.Group>
        {reason === 'Otro' && (
          <Input.TextArea
            value={reasonOtro}
            onChange={e => setReasonOtro(e.target.value)}
            placeholder='Escribí el motivo…'
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={300}
            style={{ marginTop: 10 }}
          />
        )}
      </Modal>
    </div>
  );
};
